import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { AppContext, IAppContext } from 'appState/appContext';
import { IItemForGetShortInfoWithStatus, UserRoles, IGetRestaurantsShort } from 'types';
import { useFetchData } from 'hooks/useFetchData';
import { getRestaurantsByDishShortInfo } from 'backend/restaurants';
import { useRoleAccess } from 'hooks/useRoleAccess';
import SelectedItem from 'components/SelectedItem/SelectedItem';
import { MultipleSelect } from './MultipleSelect';
import style from './RestaurantMultipleSelect.module.scss';

interface IProps {
	setFetchedRestaurants: (value: IItemForGetShortInfoWithStatus[]) => void;
	setSelectedRestaurants: (value: string[]) => void;
	selectedRestaurants: string[];
	isApplyToOne: boolean;
	IDSelectedRestaurant: string;
	dishId: string;
}

export const RestaurantMultipleSelect: React.FC<IProps> = ({
	setFetchedRestaurants,
	setSelectedRestaurants,
	selectedRestaurants,
	isApplyToOne,
	IDSelectedRestaurant,
	dishId,
}) => {
	const { loggedUser } = useContext<IAppContext>(AppContext);
	const hasAccess = useRoleAccess([
		UserRoles.SUPER_USER,
		UserRoles.CLIENT_MANAGER,
		UserRoles.GROUP_MANAGER,
		UserRoles.TECHNICAL,
	]);
	const { response, loading, refetch, responseError } = useFetchData<IGetRestaurantsShort, string>(
		dishId ? getRestaurantsByDishShortInfo : null,
		dishId || '',
	);

	const additionalRestaurants = useMemo(() => {
		const accessibleRestaurantIDs = loggedUser.restaurants ? loggedUser.restaurants.map((el) => el.id) : [];
		const restaurantsFromServer = response?.data.restaurants.filter((el) => el.id !== IDSelectedRestaurant) || [];
		const intersectedRestaurants = restaurantsFromServer.filter((el) => accessibleRestaurantIDs.includes(el.id));
		return hasAccess ? restaurantsFromServer : intersectedRestaurants;
	}, [response, loggedUser, hasAccess, IDSelectedRestaurant]);

	const handleRemove = useCallback(
		(id: string) => {
			const selectedItems = selectedRestaurants.filter((el) => el !== id);
			setSelectedRestaurants(selectedItems);
		},
		[setSelectedRestaurants, selectedRestaurants],
	);

	const restaurantsList = useMemo(() => {
		return selectedRestaurants.map((el) => {
			const label = additionalRestaurants.find((item) => item.id === el)?.name || '';
			return <SelectedItem key={el} label={label} onDelete={() => handleRemove(el)} />;
		});
	}, [handleRemove, selectedRestaurants, additionalRestaurants]);

	useEffect(() => {
		additionalRestaurants && setFetchedRestaurants(additionalRestaurants);
	}, [additionalRestaurants]);

	return (
		<div className={style.container} data-test="RestaurantField">
			<MultipleSelect
				data={additionalRestaurants}
				isLoading={loading}
				refetch={refetch}
				responseError={responseError}
				isApplyToOne={isApplyToOne}
				setSelectedRestaurants={setSelectedRestaurants}
				selectedRestaurants={selectedRestaurants}
			/>
			{!loading && <div className={style.groupsList}>{restaurantsList}</div>}
		</div>
	);
};
